import { useState } from 'react';
import { Button } from './button';

// Reusable destructive-action confirmation. The user must type DELETE
// exactly before the (red) confirm button enables — a deliberate friction
// gate for irreversible hard deletes (Plan / Rollout). Cancel is always
// available. Modal shell matches the existing in-app pattern
// (PlanTasks.ConfirmDeleteModal): fixed overlay + centred card.
//
// `impactMessage` spells out the blast radius (e.g. "…including 76
// AccountCampaign rows.") so the operator sees consequences before
// confirming. `busy` disables both buttons while the request is in flight.

const CONFIRM_WORD = 'DELETE';

export function TypeDeleteConfirmationModal({
  entityType,
  entityName,
  impactMessage,
  busy = false,
  onCancel,
  onConfirm,
}: {
  entityType: 'Plan' | 'Rollout';
  entityName: string;
  impactMessage: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const armed = typed === CONFIRM_WORD;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Delete ${entityType}`}
    >
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-slate-900">
          Delete {entityType}?
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          This permanently deletes{' '}
          <span className="font-medium text-slate-900">{entityName}</span> and
          cannot be undone.
        </p>
        <p className="mt-2 text-sm text-slate-600">{impactMessage}</p>

        <label className="mt-4 block">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span>{' '}
            to confirm
          </span>
          <input
            type="text"
            value={typed}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={busy}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && armed && !busy) onConfirm();
              if (e.key === 'Escape' && !busy) onCancel();
            }}
            placeholder={CONFIRM_WORD}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
          />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!armed || busy}
            variant="destructive"
          >
            {busy ? 'Deleting…' : `Delete ${entityType}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
